// Copyright (c) 2025, California Institute of Technology and contributors
//
// You should have received a copy of the licensing terms for this
// software included in the file "LICENSE" located in the top-level
// directory of this package. If you did not, you can view a copy at
// https://git.ligo.org/ngdd/arrakis-js/-/raw/main/LICENSE
import * as Arrow from "apache-arrow";
import { concatenate, SeriesBlock } from "./block.js";
import { Channel, type ChannelMessage } from "./channel.js";
import { DEFAULT_ARRAKIS_SERVER } from "./constants.js";

/**
 * Client to fetch or publish timeseries.
 *
 * @param url - The URL to connect to.
 *              If the URL is not set, connect to a default server
 *              or one set by ARRAKIS_SERVER.
 */
export default class Client {
  /** The URL to connect to the Arrakis server */
  public readonly initialUrl: string;

  /**
   * Creates a new Client instance.
   *
   * @param url - The URL to connect to. If not provided, will use the ARRAKIS_SERVER
   *              environment variable or default to /arrakis
   */
  constructor(url?: string | null) {
    this.initialUrl = Client.parseUrl(url);
  }

  /**
   * Count channels matching a set of conditions
   *
   * @param {string} [pattern] - Channel pattern to match channels with, using regular expressions
   * @param {string|Array<string>} [dataType] - If set, find all channels with these data types
   * @param {number} [minRate] - Minimum sample rate for channels
   * @param {number} [maxRate] - Maximum sample rate for channels
   * @param {string|Array<string>} [publisher] - If set, find all channels associated with these publishers
   * @returns {Promise<number>} A promise that resolves to the number of channels matching query
   */
  async count(
    pattern?: string,
    dataType?: string | Array<string>,
    minRate?: number,
    maxRate?: number,
    publisher?: string | Array<string>,
  ): Promise<number> {
    const params = new URLSearchParams();
    if (pattern) params.append("pattern", pattern);
    if (dataType) {
      if (Array.isArray(dataType)) {
        params.append("dataType", dataType.join(","));
      } else {
        params.append("dataType", dataType);
      }
    }
    if (minRate) params.append("minRate", minRate.toString());
    if (maxRate) params.append("maxRate", maxRate.toString());
    if (publisher) {
      if (Array.isArray(publisher)) {
        params.append("publisher", publisher.join(","));
      } else {
        params.append("publisher", publisher);
      }
    }
    const countUrl = `${this.initialUrl}/count?${params.toString()}`;
    const response = await fetch(countUrl);
    if (!response.ok) {
      throw new Error(await Client.fetchErrorMessage(response));
    }
    const body = await response.json();
    return body.count;
  }

  /**
   * Find channels matching a set of conditions and stream results via Server-Sent Events (SSE)
   *
   * @param {string} [pattern] - Channel pattern to match channels with, using regular expressions
   * @param {string|Array<string>} [dataType] - If set, find all channels with these data types
   * @param {number} [minRate] - Minimum sample rate for channels
   * @param {number} [maxRate] - Maximum sample rate for channels
   * @param {string|Array<string>} [publisher] - If set, find all channels associated with these publishers
   * @returns {AsyncGenerator<Channel>} An async generator that yields channel objects as they are received via SSE
   */
  async *find(
    pattern?: string,
    dataType?: string | Array<string>,
    minRate?: number,
    maxRate?: number,
    publisher?: string | Array<string>,
  ): AsyncGenerator<Channel> {
    // Build query parameters
    const params = new URLSearchParams();
    if (pattern) params.append("pattern", pattern);
    if (dataType) {
      if (Array.isArray(dataType)) {
        params.append("dataType", dataType.join(","));
      } else {
        params.append("dataType", dataType);
      }
    }
    if (minRate) params.append("minRate", minRate.toString());
    if (maxRate) params.append("maxRate", maxRate.toString());
    if (publisher) {
      if (Array.isArray(publisher)) {
        params.append("publisher", publisher.join(","));
      } else {
        params.append("publisher", publisher);
      }
    }
    const findUrl = `${this.initialUrl}/find?${params.toString()}`;

    const eventSource = new EventSource(findUrl);
    let done = false;

    // Queue to store channels as they arrive
    const channelQueue: Channel[] = [];

    eventSource.onmessage = (e) => {
      const message: ChannelMessage = JSON.parse(e.data);
      if (message.done) {
        done = true;
        eventSource.close();
        return;
      }
      if (message.channel) {
        const channel = Channel.fromJson(message.channel);
        channelQueue.push(channel);
      }
    };

    eventSource.onerror = (err: Event) => {
      console.error("SSE connection error:", err);
      eventSource.close();
      done = true;
    };

    try {
      while (!done || channelQueue.length > 0) {
        if (channelQueue.length > 0) {
          // Yield the next channel from the queue
          yield channelQueue.shift() as Channel;
        } else if (!done) {
          // Wait for channels to arrive by yielding control back to the event loop
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      }
    } finally {
      eventSource.close();
    }
  }

  /**
   * Stream timeseries data for a list of channels within a time range
   *
   * @param {string[]} channels - A list of channels to request
   * @param {number} [start] - GPS start time, in seconds; streams from now if omitted
   * @param {number} [end] - GPS end time, in seconds; streams indefinitely if omitted
   * @returns {AsyncGenerator<SeriesBlock>} An async generator yielding `SeriesBlock`s as they stream in
   */
  async *stream(
    channels: string[],
    start?: number,
    end?: number,
  ): AsyncGenerator<SeriesBlock> {
    const params = new URLSearchParams();
    params.append("channels", channels.join(","));
    if (start !== undefined) params.append("start", start.toString());
    if (end !== undefined) params.append("end", end.toString());

    // Create a stream reader for Arrow record batches
    const response = await fetch(
      `${this.initialUrl}/stream?${params.toString()}`,
    );
    if (!response.ok) {
      throw new Error(await Client.fetchErrorMessage(response));
    }
    if (!response.body)
      throw new Error("Failed to fetch data: empty response body");
    const reader = await Arrow.RecordBatchStreamReader.from(response.body);
    for await (const batch of reader) {
      yield SeriesBlock.fromBatch(batch);
    }
  }

  /**
   * Fetch timeseries data for a list of channels within a time range
   *
   * @param {string[]} channels - A list of channels to request
   * @param {number} start - GPS start time, in seconds
   * @param {number} end - GPS end time, in seconds
   * @returns {Promise<SeriesBlock>} A promise that resolves to the block of timeseries data
   */
  async fetch(
    channels: string[],
    start: number,
    end: number,
  ): Promise<SeriesBlock> {
    const params = new URLSearchParams();
    params.append("channels", channels.join(","));
    params.append("start", start.toString());
    params.append("end", end.toString());

    const series = this.stream(channels, start, end);
    const blocks: SeriesBlock[] = [];
    for await (const block of series) {
      blocks.push(block);
    }
    return concatenate(blocks);
  }

  /**
   * Get channel metadata for channels requested
   *
   * @param {string[]} channels - List of channels to request
   * @returns {Promise<Record<string, Channel>>} A promise that resolves to a mapping of channel names to channel metadata
   */
  async describe(channels: string[]): Promise<Record<string, Channel>> {
    const params = new URLSearchParams();
    params.append("channels", channels.join(","));

    const response = await fetch(
      `${this.initialUrl}/describe?${params.toString()}`,
    );
    if (!response.ok) {
      throw new Error(await Client.fetchErrorMessage(response));
    }
    const responseBody = await response.json();

    const result: Record<string, Channel> = {};
    for (const [name, data] of Object.entries(responseBody)) {
      result[name] = Channel.fromJson(data);
    }
    return result;
  }

  /**
   * Build a human-readable message from a failed HTTP response (JSON `{ detail }` or raw body).
   */
  private static async fetchErrorMessage(response: Response): Promise<string> {
    const statusLine = `(${response.status}${response.statusText ? ` ${response.statusText}` : ""})`;
    const text = await response.text();
    let message = statusLine;
    if (text) {
      try {
        const data = JSON.parse(text) as { detail?: unknown };
        if (data && typeof data === "object" && data.detail != null) {
          message = `${statusLine} ${data.detail as string}`;
        }
      } catch {
        message = `${statusLine}: ${text.length > 300 ? `${text.slice(0, 300)}…` : text}`;
      }
    }
    return message;
  }

  /**
   * Parses and validates a URL, falling back to environment variable or default server
   * @param {string | null | undefined} url - The URL to parse, or null/undefined to use environment/default
   * @returns {string} The parsed and validated URL
   */
  private static parseUrl(url?: string | null): string {
    // If URL is provided, validate and return it
    if (url) {
      try {
        new URL(url);
        return url;
      } catch (_error) {
        throw new Error(`Invalid URL provided: ${url}`);
      }
    }

    // Check for ARRAKIS_SERVER environment variable (Node.js)
    if (
      typeof process !== "undefined" &&
      process.env &&
      process.env.ARRAKIS_SERVER
    ) {
      const envUrl = process.env.ARRAKIS_SERVER;
      try {
        new URL(envUrl);
        return envUrl;
      } catch (_error) {
        console.warn(
          `Invalid ARRAKIS_SERVER environment variable: ${envUrl}, using default`,
        );
      }
    }

    // Return default server
    return DEFAULT_ARRAKIS_SERVER;
  }
}
